import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Printer, Search, TrendingUp } from "lucide-react";
import { PageHeader } from "../components/common";
import { AskMari } from "../components/dashboard/MetricTip";
import { CHAPTERS, FAQ, ROUTINE } from "../lib/manualContent";
import { Templates } from "../components/manual/Templates";
import { Playbooks } from "../components/manual/Playbooks";
import { CaseStudies } from "../components/manual/CaseStudies";
import { Timeline } from "../components/manual/Timeline";
import { EvidenceScorer } from "../components/manual/EvidenceScorer";
import { Prevention } from "../components/manual/Prevention";
import { RoiCalc } from "../components/manual/RoiCalc";
import { Benchmarks, MerchantFaq, Tools } from "../components/manual/Reference";

const TABS = [["guide", "Step-by-step guide"], ["templates", "Templates"], ["playbooks", "Industry playbooks"], ["cases", "Case studies"],
  ["timeline", "Timeline & decisions"], ["score", "Evidence score"], ["prevention", "Prevention"], ["roi", "ROI calculator"],
  ["benchmarks", "Benchmarks"], ["tools", "Tools"], ["faq", "Merchant FAQ"]];
const VIEWS = { templates: Templates, playbooks: Playbooks, cases: CaseStudies, timeline: Timeline, score: EvidenceScorer, prevention: Prevention, roi: RoiCalc, benchmarks: Benchmarks, tools: Tools, faq: MerchantFaq };

const TabBar = ({ tab, setTab }) => (
  <div className="flex gap-1 overflow-x-auto border-b border-line mb-8 print:hidden" data-testid="manual-tabs">
    {TABS.map(([id, l]) => (
      <button key={id} onClick={() => setTab(id)} data-testid={`manual-tab-${id}`}
        className={`shrink-0 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === id ? "border-brand text-brand" : "border-transparent text-white/55 hover:text-white"}`}>{l}</button>
    ))}
  </div>
);

const LS = "aegis-manual-done";
const hit = (q, ...p) => !q || p.join(" ").toLowerCase().includes(q);

const Step = ({ id, n, title, body, to, done, toggle }) => (
  <li className={`flex gap-4 p-4 border transition-colors ${done ? "border-brand/30 bg-brand/[0.03]" : "border-line"}`} data-testid={`manual-step-${id}`}>
    <button onClick={toggle} className="shrink-0 mt-0.5" title="Mark as done" data-testid={`manual-step-toggle-${id}`}>
      {done ? <CheckCircle2 className="w-5 h-5 text-brand" /> : <Circle className="w-5 h-5 text-white/30" />}
    </button>
    <div className="flex-1">
      <p className="font-bold"><span className="font-mono text-[11px] text-white/40 mr-2">{n}</span>{title}</p>
      <p className="text-sm text-white/65 mt-1.5">{body}</p>
      <div className="flex gap-5 mt-2 print:hidden">
        <Link to={to} className="font-mono text-[10px] tracking-wider text-brand hover:underline" data-testid={`manual-go-${id}`}>GO THERE →</Link>
        <AskMari q={`Walk me through: ${title}`} testid={`manual-ask-${id}`} />
      </div>
    </div>
  </li>
);

export default function UserManual() {
  const [params, setParams] = useSearchParams();
  const tab = VIEWS[params.get("tab")] ? params.get("tab") : "guide";
  const setTab = (t) => setParams(t === "guide" ? {} : { tab: t });
  const [q, setQ] = useState("");
  const [done, setDone] = useState(() => JSON.parse(localStorage.getItem(LS) || "{}"));
  const toggle = (id) => { const n = { ...done, [id]: !done[id] }; setDone(n); localStorage.setItem(LS, JSON.stringify(n)); };
  const s = q.trim().toLowerCase();
  const chapters = useMemo(() => CHAPTERS.map((c) => ({ ...c, steps: c.steps.map((st, i) => ({ id: `${c.id}-${i}`, n: `${c.title.split(" ")[0]}.${i + 1}`, title: st[0], body: st[1], to: st[2] }))
    .filter((st) => hit(s, c.title, st.title, st.body)) })).filter((c) => c.steps.length), [s]);
  const total = CHAPTERS.reduce((a, c) => a + c.steps.length, 0);
  const complete = Object.values(done).filter(Boolean).length;

  return (
    <div data-testid="user-manual">
      <PageHeader eyebrow="// step by step · maximum revenue capture" title="User Manual">
        <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 border border-white/15 font-mono text-[10px] tracking-wider hover:border-brand transition-colors print:hidden" data-testid="manual-print-btn"><Printer className="w-3.5 h-3.5" /> PRINT / PDF</button>
      </PageHeader>
      <TabBar tab={tab} setTab={setTab} />
      {tab !== "guide" && (() => { const V = VIEWS[tab]; return <div className="rise" key={tab} data-testid={`manual-view-${tab}`}><V /></div>; })()}
      {tab === "guide" && <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 self-start space-y-4 print:hidden" data-testid="manual-toc">
          <div className="panel p-4">
            <p className="label-xs">Your progress</p>
            <p className="font-mono text-2xl font-extrabold text-brand mt-1" data-testid="manual-progress">{complete}/{total}</p>
            <div className="h-1 bg-white/5 mt-2"><div className="h-full bg-brand transition-[width] duration-500" style={{ width: `${(100 * complete) / total}%` }} /></div>
          </div>
          <nav className="space-y-1">
            {CHAPTERS.map((c) => <a key={c.id} href={`#m-${c.id}`} className="block text-sm text-white/55 hover:text-brand transition-colors py-1" data-testid={`manual-toc-${c.id}`}>{c.title}</a>)}
            <a href="#m-routine" className="block text-sm text-white/55 hover:text-brand py-1" data-testid="manual-toc-routine">13 · Revenue routine</a>
            <a href="#m-faq" className="block text-sm text-white/55 hover:text-brand py-1" data-testid="manual-toc-faq">14 · Troubleshooting</a>
          </nav>
        </aside>
        <div className="min-w-0">
          <label className="flex items-center gap-3 border border-white/15 px-4 py-3 focus-within:border-brand transition-colors print:hidden">
            <Search className="w-4 h-4 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search the manual: "intercept", "simulate", "webhook", "PIN"…' className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="manual-search" />
          </label>
          {!chapters.length && <p className="text-sm text-white/45 mt-6" data-testid="manual-no-results">No matching steps. Try another word or ask MARI.</p>}
          {chapters.map((c) => (
            <section key={c.id} id={`m-${c.id}`} className="mt-10 scroll-mt-24" data-testid={`manual-chapter-${c.id}`}>
              <h2 className="text-2xl font-extrabold">{c.title}</h2>
              <p className="flex items-center gap-2 text-sm text-accent2 mt-2"><TrendingUp className="w-4 h-4" /> {c.lever}</p>
              <ol className="mt-4 space-y-3">{c.steps.map((st) => <Step key={st.id} {...st} done={!!done[st.id]} toggle={() => toggle(st.id)} />)}</ol>
            </section>
          ))}
          {!s && <>
            <section id="m-routine" className="mt-12 scroll-mt-24" data-testid="manual-routine">
              <h2 className="text-2xl font-extrabold">13 · Your revenue routine</h2>
              <div className="grid md:grid-cols-3 gap-4 mt-4">{ROUTINE.map(([t, items]) => (
                <div key={t} className="panel p-5"><p className="label-xs text-brand">{t}</p><ul className="mt-3 space-y-2 text-sm text-white/75">{items.map((i) => <li key={i}>□ {i}</li>)}</ul></div>))}</div>
            </section>
            <section id="m-faq" className="mt-12 scroll-mt-24" data-testid="manual-faq">
              <h2 className="text-2xl font-extrabold">14 · Troubleshooting</h2>
              <dl className="mt-4 space-y-4">{FAQ.map(([k, v]) => <div key={k} className="border-l-2 border-brand pl-4"><dt className="font-bold">{k}</dt><dd className="text-sm text-white/65 mt-1">{v}</dd></div>)}</dl>
            </section>
          </>}
        </div>
      </div>}
    </div>
  );
}
